import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { api } from '_api/base';
import { ApiResponseError, mutationType } from '_api/types';
import { AxiosRequestConfig } from 'axios';
import { isFunction } from 'lodash';

function makeApiCall<T extends object, F extends object, G extends object>(
  requestType: mutationType,
  getEndpoint: (() => string) | ((x: F) => string),
  requestOptions?: AxiosRequestConfig,
  getBody?: (x: F) => G,
): (x: F) => Promise<T> {
  return async (requestBody: F) => {
    let body: F | G;
    if (getBody && isFunction(getBody)) {
      body = getBody(requestBody);
    } else {
      body = requestBody;
    }
    return api[requestType](getEndpoint(requestBody), body, requestOptions);
  };
}

const useApiMutation = <T extends object, F extends object, G extends object = {}>(
  getEndpoint: (() => string) | ((x: F) => string),
  requestType: mutationType,
  requestOptions: AxiosRequestConfig | undefined,
  mutationOptions: UseMutationOptions<T, ApiResponseError, F>,
  getBody?: (x: F) => G,
) =>
  useMutation<T, ApiResponseError, F>({
    ...mutationOptions,
    mutationFn: makeApiCall(requestType, getEndpoint, requestOptions, getBody),
  });

export default useApiMutation;
